import * as React from 'react';
import { throttle } from 'throttle-debounce';
import {
  ROAD_SAVED_FILTERS,
  SavedFiltersIds,
  STATION_SAVED_FILTERS,
  UNIQUE_SAVED_FILTERS,
} from '../../../interfaces/filters';
import { ISavedFilter } from '../../../store/saved_filters/actions';
import { isArrayWithItems } from '../../../utils';
import { getDirectionsName } from '../../../utils/stations';
import { trackSavedRoadDeleted } from '../../roads/tracking';
import { ISavedFiltersDispatchProps, ISavedFiltersStoreProps } from './container';
import { FitInOneLine } from './FitInOneLine';
import { getItemName } from './helpers';

import * as styles from './index.css';
import { groupBy } from '../../../utils/groupBy';
import { Tag } from '../../Tag';

export type ISavedFiltersProps = ISavedFiltersStoreProps & ISavedFiltersDispatchProps;

interface ISavedFiltersState {
  maxWidth: number;
}

export class SavedFilters extends React.Component<ISavedFiltersProps, ISavedFiltersState> {
  private wrapper: HTMLDivElement | null = null;
  public state = {
    maxWidth: 0,
  };

  public onResize = () => this.setState({ maxWidth: this.wrapper ? this.wrapper.clientWidth : 0 });

  public throttleTime = 50;

  public resizeHandler = throttle(this.throttleTime, this.onResize);

  public componentDidMount() {
    this.resizeHandler();
    window.addEventListener('resize', this.resizeHandler);
  }

  public componentDidUpdate() {
    if (this.wrapper && !this.state.maxWidth) {
      this.onResize();
    }
  }

  public render() {
    const {
      savedFilters: { items, showAll },
      onChangeFitInOneLine,
    } = this.props;
    const { maxWidth } = this.state;
    if (!isArrayWithItems(items)) {
      return null;
    }
    const modifiedItems = this.renameSameNameStations(items);

    return (
      <div ref={wrapper => (this.wrapper = wrapper)} className={styles['list']}>
        <FitInOneLine
          enabled={showAll}
          maxWidth={maxWidth}
          onItemsClear={this.props.onRemoveAllSavedFilters}
          toggle={onChangeFitInOneLine}
          items={modifiedItems}
        >
          {modifiedItems.map(this.renderFilters)}
        </FitInOneLine>
      </div>
    );
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
  }

  private renderFilters = (item: ISavedFilter) => {
    return (
      <div className={styles['list-item']} key={UNIQUE_SAVED_FILTERS.includes(item.id) ? item.id : item.id + item.name}>
        <Tag onDelete={() => this.handleSaveItemRemove(item)}>{getItemName(item)}</Tag>
      </div>
    );
  };

  private renameSameNameStations = (filters: ISavedFilter[]): ISavedFilter[] => {
    const { stationDirectionsMap } = this.props;
    const modifiedFilters: ISavedFilter[] = [...filters];
    const valueIndexMap: { [stationId: number]: number } = {};
    const groupedFilters = groupBy(modifiedFilters, 'name');

    modifiedFilters.forEach((filter, index) => {
      if (!filter.value || typeof filter.value !== 'number') {
        return;
      }
      valueIndexMap[filter.value] = index;
    });

    Object.keys(groupedFilters).forEach(key => {
      const group = groupedFilters[key];
      // Если фильтр не по станции или нет одинаковых названий - пропускаем
      if (group.length <= 1 || group[0].id !== SavedFiltersIds.Station) {
        return;
      }

      group.forEach(filter => {
        if (!filter.value || typeof filter.value !== 'number') {
          return;
        }
        const index = valueIndexMap[filter.value];
        const stationId = filter.value;
        const directions = stationDirectionsMap[stationId];
        const newName = `${filter.name} (${getDirectionsName(directions)} напр.)`;
        modifiedFilters[index] = { ...modifiedFilters[index], name: newName };
      });
    });

    return modifiedFilters;
  };

  private handleSaveItemRemove = (item: ISavedFilter) => {
    const isRoadSavedFilter = ROAD_SAVED_FILTERS.includes(item.id);
    const isStationSavedFilter = STATION_SAVED_FILTERS.includes(item.id);

    if (isRoadSavedFilter) {
      trackSavedRoadDeleted();
    }

    const {
      removeSavedFilter,
      savedFilters: { items },
    } = this.props;

    if (isRoadSavedFilter && items.filter(({ id }) => ROAD_SAVED_FILTERS.includes(id)).length === 1) {
      const roadDistanceSavedFilter = items.find(({ id }) => id === SavedFiltersIds.RoadDistance);
      if (roadDistanceSavedFilter) {
        removeSavedFilter(roadDistanceSavedFilter);
      }
    } else if (isStationSavedFilter && items.filter(({ id }) => STATION_SAVED_FILTERS.includes(id)).length === 1) {
      const stationDistanceSavedFilter = items.find(({ id }) => id === SavedFiltersIds.StationDistance);
      if (stationDistanceSavedFilter) {
        removeSavedFilter(stationDistanceSavedFilter);
      }
    } else {
      removeSavedFilter(item);
    }
  };
}
